import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils'
import {importsQueryKey, storageUsageQueryKey} from '@/shared/utils/hooks/useAPIFiles'
import {useQueryClient} from '@tanstack/react-query'
import {Auth} from 'aws-amplify'
import React, {createContext, FC, ReactNode, useCallback, useEffect, useRef, useState} from 'react'
import {Id, toast} from 'react-toastify'
import {useCognitoAuth} from '../../auth'
import {dispatchChatUpdated, parseChatUpdatedMessage} from './ai-chat/chatUpdated'
import {filesChangedFolders, subscribeChatFilesChanged} from './ai-chat/filesChanged'
import {dispatchJobStatus, parseJobStatusMessage} from './ai-chat/jobStatus'
import {resolvePendingRun} from './ai-chat/pendingRuns'
import type {TransferOutcomeStatus} from './ai-chat/transferContinuation'
import {fileQueryKeys} from '../core/storage'
import {invalidateChangedFolders, invalidateSharedFolder} from './fileListInvalidation'
import {applyShareEvent, isShareMessage, parseShareEvent} from '../../sharing/shareEvents'
import {
  importFinishedToast,
  isImportActive,
  markImportStatusAnnounced,
  parseImportStatusMessage,
} from './imports/importStatus'

const SocketContext = createContext<
  {socket: WebSocket | null; updateInfiniteToast: (toastId: Id | undefined) => void} | undefined
>(undefined)

const SocketProvider: FC<{children: ReactNode}> = ({children}: {children: ReactNode}) => {
  const [reconnectAttempts, setReconnectAttempts] = useState(0)
  const [socket, setSocket] = useState<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const queryClient = useQueryClient()
  const infiniteToast = useRef<Id | undefined>(undefined)
  // Kept in a ref so the socket onmessage closure always sees the current identityId without
  // re-creating the connection when auth resolves.
  const {currentUser} = useCognitoAuth()
  const identityIdRef = useRef<string | undefined>(currentUser?.identity_id)
  useEffect(() => {
    identityIdRef.current = currentUser?.identity_id
  }, [currentUser])

  // A chat turn that wrote files reports them in `files_changed`; refresh those folders here too,
  // so the Files panel updates even when the `files.changed` notification does not arrive.
  useEffect(
    () =>
      subscribeChatFilesChanged((changes) =>
        invalidateChangedFolders(queryClient, identityIdRef.current, filesChangedFolders(changes))
      ),
    [queryClient]
  )

  const connectSocket = useCallback(async () => {
    let nextSocket: WebSocket | null = null
    try {
      nextSocket = await openConnection()
    } catch (error: any) {
      getLogger().error(`Websocket connection failed due to: ${error.message}`)
    }

    if (nextSocket) {
      nextSocket.onopen = () => {}
      nextSocket.onerror = (err) => {
        infiniteToast.current &&
          toast.update(infiniteToast.current, {
            render: 'Action failed, please try again later',
            type: 'error',
            isLoading: false,
            autoClose: 3000,
          })
      }
      nextSocket.onmessage = (ev) => {
        const data = JSON.parse(ev.data)
        const message = data?.message
        if (isShareMessage(message)) {
          // Sharing changed (an invitation, members, access, or a share that went away): refetch
          // the sharing views. Read before anything else and never as a change in the user's own
          // files; a `share.*` type this client doesn't know is ignored.
          const shareEvent = parseShareEvent(message)
          if (shareEvent) {
            applyShareEvent(queryClient, shareEvent)
          }
          return
        }
        const jobStatus = parseJobStatusMessage(message)
        const chatUpdated = parseChatUpdatedMessage(message)
        const importStatus = parseImportStatusMessage(message)
        if (jobStatus) {
          // A cloud job reached a terminal state. Mark it here rather than in the chat panel: this
          // provider is always mounted, the panel is not (it unmounts when closed), and the
          // conversation's session may not be open. Marking is what survives; the panel dispatches
          // the follow-up turn when it can, now or on its next mount.
          resolvePendingRun(jobStatus.runId, jobStatus.status)
          dispatchJobStatus(jobStatus)
          // Same nudge as `{jobs}`: history / balance / usage are REST-backed — invalidate so the
          // dashboard (and Jobs page) refetch instead of waiting on staleTime.
          queryClient.invalidateQueries(['history'])
          queryClient.invalidateQueries(['balance'])
          queryClient.invalidateQueries(storageUsageQueryKey)
        } else if (chatUpdated) {
          // Another device (or the agent on its own) added a turn to a conversation. The chat
          // socket never tells this client about it, so hand it over to refetch.
          dispatchChatUpdated(chatUpdated)
        } else if (importStatus) {
          // A server-side import (S3 / HTTPS / FTP) started or ended. Refresh the Imports list; at
          // the end also refresh the destination folder, since a large import's files can arrive
          // before their own debounced `files.changed`.
          queryClient.invalidateQueries(importsQueryKey)
          if (!isImportActive(importStatus.status)) {
            // Also refreshes storage usage.
            invalidateChangedFolders(queryClient, identityIdRef.current, [importStatus.destinationPath])
            const finished = importFinishedToast(importStatus)
            if (finished && markImportStatusAnnounced(importStatus)) {
              toast[finished.type](finished.text, {
                ...toastDefaultConfig,
                toastId: `import-${importStatus.importId}-${importStatus.status}`,
              })
            }
            // A transfer the chat started (start_transfer) is one of its pending runs: resolve it like
            // a finished job, so an open chat continues. Imports started in Files resolve nothing.
            const runStatus = importStatus.status === 'failed' ? 'failed' : 'complete'
            const transfer = {
              status: importStatus.status as TransferOutcomeStatus,
              destinationPath: importStatus.destinationPath,
              copiedFiles: importStatus.copiedFiles,
              failedFiles: importStatus.failedFiles,
            }
            if (resolvePendingRun(importStatus.importId, runStatus, transfer)) {
              dispatchJobStatus({runId: importStatus.importId, status: runStatus})
            }
          }
        } else if (message && message.jobs) {
          queryClient.invalidateQueries(['history'])
          queryClient.invalidateQueries(['balance'])
        } else if (message && message.type === 'files.changed') {
          const changedPath = typeof message.path === 'string' ? message.path : ''
          if (message.shareId !== undefined) {
            // A change in a folder someone shares with this user: `path` is relative to that
            // shared folder, not to the user's own space, so refresh that share's listings only.
            // Read before anything else: treated as an own-space path it would refresh (and
            // count towards storage usage) a same-named folder of the user's own.
            if (typeof message.shareId === 'string' && message.shareId) {
              invalidateSharedFolder(queryClient, message.shareId, changedPath)
            }
            return
          }
          // Targeted refresh: invalidate only the changed folder and its ancestors (built in
          // FileTable's `${identityId}/${relativePath}` key form), leaving unrelated folders
          // cached. The backend sends the affected folder as a relative path.
          invalidateChangedFolders(queryClient, identityIdRef.current, [changedPath])
        } else if (message && typeof message === 'object' && Object.keys(message).length === 0) {
          // Back-compat: coarse "something changed" signal (whole file browser).
          queryClient.invalidateQueries(fileQueryKeys.all)
          queryClient.invalidateQueries(storageUsageQueryKey)
          infiniteToast.current &&
            toast.update(infiniteToast.current, {
              render: 'Action completed',
              type: 'success',
              isLoading: false,
              autoClose: 3000,
            })
        }
      }
      nextSocket.onclose = (ev) => {
        nextSocket?.close(1000, 'Socket disconnected')
        setSocket(null)
        if (ev.reason !== 'logout' && reconnectTimeoutRef.current === null) {
          const nextReconnectAttempts = reconnectAttempts + 1

          setReconnectAttempts(nextReconnectAttempts)
          if (nextReconnectAttempts < 5) {
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectTimeoutRef.current = null
              if (nextSocket?.CLOSED) {
                connectSocket()
              }
            }, Math.min(1000 * 2 ** reconnectAttempts, 30000))
          }
        }
      }
      setSocket(nextSocket)
    }
  }, [queryClient, reconnectAttempts])

  useEffect(() => {
    connectSocket()
    return () => {
      if (reconnectTimeoutRef.current !== null) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }
  }, [connectSocket])

  const memoizedValues = React.useMemo(
    () => ({
      socket,
      updateInfiniteToast: (toastId: Id | undefined) => {
        infiniteToast.current = toastId
      },
    }),
    [socket]
  )

  return <SocketContext.Provider value={memoizedValues}>{children}</SocketContext.Provider>
}
const openConnection = async (): Promise<WebSocket | null> => {
  const wsUrl = process.env.REACT_APP_WS_URL || ''
  if (!wsUrl) {
    return null
  }
  try {
    const session = await Auth.currentSession()
    const token = session.getIdToken().getJwtToken()
    if (!token) {
      return null
    }
    return new WebSocket(`${wsUrl}?token=${token}`)
  } catch (e: unknown) {
    getLogger().error(
      `Websocket connection failed due to: ${e instanceof Error ? e.message : String(e)}`
    )
    return null
  }
}
const useSocket = (eventNames: string[], eventHandlers: EventListenerOrEventListenerObject[]) => {
  const context = React.useContext(SocketContext)
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider')
  }

  useEffect(() => {
    for (let i = 0; i < eventNames.length; i++) {
      const eventName = eventNames[i]
      getLogger().info('WebSocket: adding listener', eventName)
      context.socket && context.socket.addEventListener(eventName, eventHandlers[i])
    }
    return () => {
      for (let i = 0; i < eventNames.length; i++) {
        const eventName = eventNames[i]
        getLogger().info('WebSocket: removing listener', eventName)
        context.socket && context.socket.removeEventListener(eventName, eventHandlers[i])
      }
    }
  })

  return context
}

export {SocketProvider, useSocket}
